import React from 'react';
import { LuNetwork, LuPlug, LuSunMoon, LuActivity, LuLamp, LuInfo, LuLayers } from 'react-icons/lu';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { translate } from '../../../../utils/Common';
import { getProviderCapabilities, LAMP_HOURS } from '../../../../config/providers';

const k = (key) => translate(`dashboard.platforms.providerCapabilities.${key}`);

// Una fila estilo lista de Ajustes: icono con tinte, título + descripción y estado a la derecha.
// El separador va dentro del bloque de texto, así arranca después del icono (inset de iOS).
const CapabilityRow = ({ icon: Icon, title, description, badge, badgeVariant, active }) => (
  <li className="group flex items-center gap-3 pl-4">
    <span
      className={cn(
        'flex size-8 shrink-0 items-center justify-center rounded-lg transition-colors duration-300',
        active ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground/70'
      )}
    >
      <Icon size={16} strokeWidth={2} />
    </span>
    <div className="flex min-w-0 flex-1 items-center gap-3 border-t border-border/60 py-3 pr-4 group-first:border-t-0">
      <div className="min-w-0 flex-1">
        <p className={cn('text-sm font-medium leading-5', active ? 'text-foreground' : 'text-muted-foreground')}>{title}</p>
        <p className="mt-0.5 text-xs leading-4 text-muted-foreground">{description}</p>
      </div>
      <Badge variant={badgeVariant} className="shrink-0 whitespace-nowrap px-2 text-[11px]">{badge}</Badge>
    </div>
  </li>
);

/**
 * Indicador de lo que incluye cada proveedor. Lee las capabilities del catálogo de
 * proveedores (api-boxit-admin-usa), así la pantalla y el API no se desalinean.
 */
const ProviderCapabilities = ({ providerId, providers }) => {
  const capabilities = getProviderCapabilities(providerId, providers);

  if (!capabilities) {
    return (
      <div className="col-span-12 mb-4 flex items-center gap-2 rounded-2xl border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
        <LuInfo size={16} className="shrink-0" />
        {k('empty')}
      </div>
    );
  }

  const providerName = providers?.find(provider => Number(provider.id) === Number(providerId))?.value;
  const { network, monitoring, lampHours, sharedIp, multiVendor } = capabilities;
  // Mercury: sin horas de lámpara, el tipo Indoor/Outdoor es solo informativo
  const noLampHours = lampHours === LAMP_HOURS.NONE;

  // Multi-Vendor: la conexión la pone el proveedor de cada módulo y el monitoreo el de la unidad central
  const connectionRow = multiVendor
    ? { icon: LuLayers, description: k('connection.multiVendor'), badge: k('perModule') }
    : {
      icon: network ? LuNetwork : LuPlug,
      description: network ? (sharedIp ? k('connection.sharedIp') : k('connection.network')) : k('connection.serial'),
      badge: network ? k('connection.ip') : k('connection.com'),
    };
  const monitoringRow = multiVendor
    ? { description: k('monitoring.multiVendor'), badge: k('outdoorOnly'), badgeVariant: 'info', active: true }
    : {
      description: monitoring ? k('monitoring.yes') : k('monitoring.no'),
      badge: monitoring ? k('outdoorOnly') : k('notApplicable'),
      badgeVariant: monitoring ? 'success' : 'muted',
      active: monitoring,
    };

  const rows = [
    {
      ...connectionRow,
      title: k('connection'),
      badgeVariant: 'info',
      active: true,
    },
    {
      icon: LuSunMoon,
      title: k('lockerType'),
      description: noLampHours ? k('lockerType.informative') : k('lockerType.yes'),
      badge: noLampHours ? k('informative') : k('included'),
      badgeVariant: noLampHours ? 'info' : 'success',
      active: true,
    },
    {
      icon: LuActivity,
      title: k('monitoring'),
      ...monitoringRow,
    },
    {
      icon: LuLamp,
      title: k('lampHours'),
      description: noLampHours ? k('lampHours.none') : k('lampHours.required'),
      badge: noLampHours ? k('notApplicable') : k('required'),
      badgeVariant: noLampHours ? 'muted' : 'success',
      active: !noLampHours,
    },
  ];

  return (
    <section
      key={providerId}
      aria-label={k('title')}
      className="col-span-12 mb-2 mt-1 overflow-hidden rounded-2xl border border-border/60 bg-card shadow-[0_1px_2px_rgba(0,0,0,0.04)] animate-in fade-in-0 duration-300"
    >
      <header className="flex items-baseline justify-between gap-3 border-b border-border/60 px-4 py-2.5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">{k('title')}</p>
        {providerName && <p className="truncate text-sm font-semibold text-foreground">{providerName}</p>}
      </header>
      {/* una sola columna: la tarjeta vive en un panel angosto, y dos columnas por breakpoint de viewport la aplastaban */}
      <ul>
        {rows.map(row => <CapabilityRow key={row.title} {...row} />)}
      </ul>
    </section>
  );
};

export default ProviderCapabilities;
